import {invalidateOnSuccess, omneticApi} from '@dms/api/base';

import {
  ActivateServiceAccountApiArg,
  ActivateServiceAccountApiResponse,
  CreateServiceAccountApiArg,
  CreateServiceAccountApiResponse,
  DeactivateServiceAccountApiArg,
  DeactivateServiceAccountApiResponse,
  DeleteServiceAccountApiArg,
  DeleteServiceAccountApiResponse,
  GetServiceAccountApiArg,
  GetServiceAccountApiResponse,
  ListServiceAccountKeysApiArg,
  ListServiceAccountKeysApiResponse,
  RevokeServiceAccountKeyApiArg,
  RevokeServiceAccountKeyApiResponse,
  RotateServiceAccountKeyApiArg,
  RotateServiceAccountKeyApiResponse,
  UpdateServiceAccountApiArg,
  UpdateServiceAccountApiResponse,
} from './types';

export const serviceAccountApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    createServiceAccount: build.mutation<
      CreateServiceAccountApiResponse,
      CreateServiceAccountApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/service-account`,
        method: 'POST',
        body: queryArg.createServiceAccountRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess(['ServiceAccountList']),
    }),
    getServiceAccount: build.query<GetServiceAccountApiResponse, GetServiceAccountApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/service-account/${queryArg.serviceAccountId}`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'ServiceAccount', id: queryArg.serviceAccountId},
      ],
    }),
    updateServiceAccount: build.mutation<
      UpdateServiceAccountApiResponse,
      UpdateServiceAccountApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/service-account/${queryArg.serviceAccountId}`,
        method: 'PUT',
        body: queryArg.updateServiceAccountRequestBody,
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: UpdateServiceAccountApiArg) => [
        {type: 'ServiceAccount', id: queryArg.serviceAccountId},
        'ServiceAccountList',
      ]),
    }),
    activateServiceAccount: build.mutation<
      ActivateServiceAccountApiResponse,
      ActivateServiceAccountApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/service-account/${queryArg.serviceAccountId}/activate`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: ActivateServiceAccountApiArg) => [
        {type: 'ServiceAccount', id: queryArg.serviceAccountId},
        'ServiceAccountList',
      ]),
    }),
    deactivateServiceAccount: build.mutation<
      DeactivateServiceAccountApiResponse,
      DeactivateServiceAccountApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/service-account/${queryArg.serviceAccountId}/deactivate`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: DeactivateServiceAccountApiArg) => [
        {type: 'ServiceAccount', id: queryArg.serviceAccountId},
        'ServiceAccountList',
      ]),
    }),
    deleteServiceAccount: build.mutation<
      DeleteServiceAccountApiResponse,
      DeleteServiceAccountApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/service-account/${queryArg.serviceAccountId}`,
        method: 'DELETE',
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: DeleteServiceAccountApiArg) => [
        {type: 'ServiceAccount', id: queryArg.serviceAccountId},
        'ServiceAccountList',
      ]),
    }),
    listServiceAccountKeys: build.query<
      ListServiceAccountKeysApiResponse,
      ListServiceAccountKeysApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/service-account/${queryArg.serviceAccountId}/key`,
      }),
      providesTags: (_result, _error, queryArg) => [
        {type: 'ServiceAccountKeys', id: queryArg.serviceAccountId},
      ],
    }),
    rotateServiceAccountKey: build.mutation<
      RotateServiceAccountKeyApiResponse,
      RotateServiceAccountKeyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/service-account/${queryArg.serviceAccountId}/key/rotate`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: RotateServiceAccountKeyApiArg) => [
        {type: 'ServiceAccountKeys', id: queryArg.serviceAccountId},
      ]),
    }),
    revokeServiceAccountKey: build.mutation<
      RevokeServiceAccountKeyApiResponse,
      RevokeServiceAccountKeyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/service-account/${queryArg.serviceAccountId}/key/${queryArg.keyId}/revoke`,
        method: 'POST',
      }),
      invalidatesTags: invalidateOnSuccess((_result, queryArg: RevokeServiceAccountKeyApiArg) => [
        {type: 'ServiceAccountKeys', id: queryArg.serviceAccountId},
      ]),
    }),
  }),
});

export const {
  useCreateServiceAccountMutation,
  useGetServiceAccountQuery,
  useUpdateServiceAccountMutation,
  useActivateServiceAccountMutation,
  useDeactivateServiceAccountMutation,
  useDeleteServiceAccountMutation,
  useListServiceAccountKeysQuery,
  useRotateServiceAccountKeyMutation,
  useRevokeServiceAccountKeyMutation,
} = serviceAccountApi;
